/**
 * Contenu du formulaire paiement (sans Dialog wrapper)
 * Réutilisable dans PaiementFormModal et dans le modal de clôture
 */

import { useState, useEffect, useCallback } from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Loader2, Upload, FileText, Scan, AlertCircle, Check, Info } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { z } from 'zod';
import { usePaiementOCR } from '@/hooks/usePaiementOCR';
import { useToast } from '@/hooks/use-toast';
import { formatMontant } from '@/lib/formatters';
import type {
  PaiementClient,
  PaiementFormData,
  FactureClientSimple,
  TypePaiement,
  MoyenPaiement,
  StatutPaiementItem,
} from '@/types/paiement';
import {
  LABELS_TYPE_PAIEMENT,
  LABELS_MOYEN_PAIEMENT,
  LABELS_STATUT_PAIEMENT_ITEM,
} from '@/types/paiement';


export interface PaiementFormContentProps {
  onSubmit: (data: PaiementFormData) => Promise<PaiementClient | null>;
  onCancel: () => void;
  paiement?: PaiementClient | null;
  facturesClients: FactureClientSimple[];
  totalDevis?: number;
  resteAEncaisser?: number;
}

export function PaiementFormContent({
  onSubmit,
  onCancel,
  paiement,
  facturesClients,
  totalDevis = 0,
  resteAEncaisser = 0,
}: PaiementFormContentProps) {
  const isEditing = !!paiement;

  // État du formulaire
  const [activeTab, setActiveTab] = useState<'manuel' | 'ocr'>('manuel');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Champs du formulaire
  const [montant, setMontant] = useState('');
  const [datePaiement, setDatePaiement] = useState(new Date().toISOString().split('T')[0]);
  const [type, setType] = useState<TypePaiement>('solde');
  const [moyenPaiement, setMoyenPaiement] = useState<MoyenPaiement | ''>('');
  const [reference, setReference] = useState('');
  const [statut, setStatut] = useState<StatutPaiementItem>('encaisse');
  const [notes, setNotes] = useState('');
  const [factureClientId, setFactureClientId] = useState('');
  const [fichierUrl, setFichierUrl] = useState<string | null>(null);
  const [showDetails, setShowDetails] = useState(false);

  // Hook OCR
  const {
    isExtracting,
    extractedData,
    confidence,
    fileUrl,
    error: ocrError,
    extractPaiement,
    reset: resetOCR,
    mapToMoyenPaiement,
  } = usePaiementOCR();

  // Réinitialiser le formulaire
  const resetForm = useCallback(() => {
    setMontant('');
    setDatePaiement(new Date().toISOString().split('T')[0]);
    setType('solde');
    setMoyenPaiement('');
    setReference('');
    setStatut('encaisse');
    setNotes('');
    setFactureClientId('');
    setFichierUrl(null);
    setShowDetails(false);
    setActiveTab('manuel');
    resetOCR();
  }, [resetOCR]);

  // Pré-remplir en mode édition
  useEffect(() => {
    if (paiement) {
      setMontant(paiement.montant.toString());
      setDatePaiement(paiement.date_paiement);
      setType(paiement.type);
      setMoyenPaiement(paiement.moyen_paiement || '');
      setReference(paiement.reference || '');
      setStatut(paiement.statut);
      setNotes(paiement.notes || '');
      setFactureClientId(paiement.facture_client_id || '');
      setFichierUrl(paiement.fichier_url || null);
      if (paiement.moyen_paiement || paiement.reference) {
        setShowDetails(true);
      }
    } else {
      resetForm();
    }
  }, [paiement, resetForm]);

  // Appliquer les données OCR au formulaire
  const applyOCRData = () => {
    if (!extractedData) return;

    if (extractedData.montant) {
      setMontant(extractedData.montant.toString());
    }
    if (extractedData.date_paiement) {
      setDatePaiement(extractedData.date_paiement);
    }
    if (extractedData.reference) {
      setReference(extractedData.reference);
      setShowDetails(true);
    }
    if (extractedData.type_paiement_detecte) {
      const moyenDetecte = mapToMoyenPaiement(extractedData.type_paiement_detecte);
      if (moyenDetecte) {
        setMoyenPaiement(moyenDetecte);
        setShowDetails(true);
      }
    }
    if (extractedData.libelle) {
      setNotes(extractedData.libelle);
    }
    if (fileUrl) {
      setFichierUrl(fileUrl);
    }

    // Basculer vers l'onglet manuel pour finaliser
    setActiveTab('manuel');
  };

  // Gérer l'upload de fichier
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await extractPaiement(file);
  };

  // Schema de validation Zod
  const paiementSchema = z.object({
    montant: z.number().positive("Le montant doit être positif").max(100_000_000, "Montant trop élevé"),
    date_paiement: z.string().min(1, "La date est requise"),
    type: z.enum(['acompte', 'solde', 'avance'] as const, { message: "Type de paiement invalide" }),
    statut: z.enum(['encaisse', 'en_attente', 'annule'] as const, { message: "Statut invalide" }),
    moyen_paiement: z.enum(['virement', 'cheque', 'especes', 'carte', 'prelevement'] as const).optional(),
    reference: z.string().max(200, "200 caractères max").optional(),
    notes: z.string().max(2000, "2000 caractères max").optional(),
    facture_client_id: z.string().uuid().optional(),
    fichier_url: z.string().url().optional(),
  });

  const { toast } = useToast();

  // Soumettre le formulaire
  const handleSubmit = async () => {
    const montantNum = parseFloat(montant.replace(/[^0-9.,]/g, '').replace(',', '.'));

    const validation = paiementSchema.safeParse({
      montant: isNaN(montantNum) ? 0 : montantNum,
      date_paiement: datePaiement,
      type,
      statut,
      moyen_paiement: moyenPaiement || undefined,
      reference: reference.trim() || undefined,
      notes: notes.trim() || undefined,
      facture_client_id: factureClientId || undefined,
      fichier_url: fichierUrl || undefined,
    });

    if (!validation.success) {
      validation.error.issues.forEach(issue => {
        toast({ title: "Erreur", description: issue.message, variant: "destructive" });
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const data: PaiementFormData = {
        ...validation.data,
        extraction_json: extractedData || undefined,
        confiance: confidence || undefined,
      };

      const result = await onSubmit(data);
      if (result) {
        resetForm();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as 'manuel' | 'ocr')}>
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="manuel" className="flex items-center gap-2">
          <FileText className="h-4 w-4" />
          Manuel
        </TabsTrigger>
        <TabsTrigger value="ocr" className="flex items-center gap-2" disabled={isEditing}>
          <Scan className="h-4 w-4" />
          Scan IA
        </TabsTrigger>
      </TabsList>

      {/* Onglet Manuel */}
      <TabsContent value="manuel" className="space-y-6 mt-4">
        {/* Montant avec slider */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label htmlFor="montant">Montant *</Label>
            {totalDevis > 0 && (
              <span className="text-xs">
                <span className="text-muted-foreground">Total devis : </span>
                <span className="text-foreground font-medium">{formatMontant(totalDevis)}</span>
              </span>
            )}
          </div>

          {/* Input montant */}
          <div className="relative">
            <Input
              id="montant"
              type="text"
              placeholder="0,00"
              value={montant}
              onChange={(e) => {
                const val = e.target.value.replace(/[^0-9.,]/g, '');
                setMontant(val);
              }}
              className="text-right pr-8 text-lg font-semibold"
              disabled={isSubmitting}
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground">€</span>
          </div>

          {/* Slider */}
          {totalDevis > 0 && (
            <div className="px-1">
              <Slider
                value={[parseFloat(montant.replace(',', '.')) || 0]}
                onValueChange={(values) => {
                  const val = values[0].toFixed(2).replace('.', ',');
                  setMontant(val);
                }}
                max={resteAEncaisser > 0 ? resteAEncaisser : totalDevis}
                min={0}
                step={0.01}
                disabled={isSubmitting}
                className="cursor-pointer"
              />
              <div className="flex justify-between text-xs mt-1">
                <span className="text-muted-foreground">0 €</span>
                <span className="text-foreground font-medium">{formatMontant(resteAEncaisser > 0 ? resteAEncaisser : totalDevis)}</span>
              </div>
            </div>
          )}

          {/* Boutons de suggestion rapide */}
          {resteAEncaisser > 0 && (
            <div className="flex gap-2 flex-wrap">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMontant((resteAEncaisser * 0.3).toFixed(2).replace('.', ','))}
                disabled={isSubmitting}
                className="text-xs"
              >
                30% ({formatMontant(resteAEncaisser * 0.3)})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMontant((resteAEncaisser * 0.5).toFixed(2).replace('.', ','))}
                disabled={isSubmitting}
                className="text-xs"
              >
                50% ({formatMontant(resteAEncaisser * 0.5)})
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setMontant(resteAEncaisser.toFixed(2).replace('.', ','))}
                disabled={isSubmitting}
                className="text-xs"
              >
                100% ({formatMontant(resteAEncaisser)})
              </Button>
            </div>
          )}
        </div>

        {/* Date */}
        <div>
          <Label htmlFor="date">Date *</Label>
          <Input
            id="date"
            type="date"
            value={datePaiement}
            onChange={(e) => setDatePaiement(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Type */}
        <div>
          <div className="flex items-center gap-1">
            <Label htmlFor="type">Type *</Label>
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Info className="h-4 w-4 text-muted-foreground cursor-help" />
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  <p>Avance : versé avant tout travail. Acompte : versé pendant les travaux. Solde : dernier paiement à la fin.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <Select value={type} onValueChange={(v) => setType(v as TypePaiement)} disabled={isSubmitting}>
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(LABELS_TYPE_PAIEMENT).map(([value, label]) => (
                <SelectItem key={value} value={value}>{label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Facture liée */}
        {facturesClients.length > 0 && (
          <div>
            <Label htmlFor="facture">Facture liée (optionnel)</Label>
            <Select
              value={factureClientId}
              onValueChange={setFactureClientId}
              disabled={isSubmitting}
            >
              <SelectTrigger>
                <SelectValue placeholder="Aucune facture liée" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Aucune</SelectItem>
                {facturesClients.map((facture) => (
                  <SelectItem key={facture.id} value={facture.id}>
                    {facture.numero_facture} - {formatMontant(facture.montant_ttc)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">Associer ce paiement à une facture pour le suivi comptable.</p>
          </div>
        )}

        {/* Notes */}
        <div>
          <Label htmlFor="notes">Notes</Label>
          <Input
            id="notes"
            placeholder="Notes optionnelles..."
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            disabled={isSubmitting}
          />
        </div>

        {/* Boutons */}
        <div className="flex justify-end gap-2 pt-4">
          <Button variant="ghost" onClick={onCancel} disabled={isSubmitting} className="text-white/30 bg-transparent hover:text-white/55 hover:bg-transparent">
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting || !montant || !datePaiement} className="bg-gradient-to-r from-[#F7B500] to-[#FF9500] text-black font-bold hover:brightness-110 shadow-[0_6px_20px_rgba(247,181,0,0.2)]">
            {isSubmitting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Enregistrement du paiement...
              </>
            ) : (
              isEditing ? 'Mettre à jour le paiement' : 'Enregistrer le paiement'
            )}
          </Button>
        </div>
      </TabsContent>

      {/* Onglet OCR */}
      <TabsContent value="ocr" className="space-y-4 mt-4">
        {/* Zone d'upload */}
        {!extractedData && !isExtracting && (
          <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
            <Upload className="h-12 w-12 mx-auto text-muted-foreground/50 mb-4" />
            <p className="text-sm text-muted-foreground mb-4">
              Glissez un justificatif de paiement (PDF, JPG, PNG)
              <br />
              ou cliquez pour sélectionner un fichier
            </p>
            <Input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
              id="file-upload-content"
            />
            <Button
              variant="outline"
              onClick={() => document.getElementById('file-upload-content')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              Sélectionner un fichier
            </Button>
          </div>
        )}

        {/* Extraction en cours */}
        {isExtracting && (
          <div className="text-center py-8">
            <Loader2 className="h-12 w-12 mx-auto animate-spin text-primary mb-4" />
            <p className="text-muted-foreground">Extraction en cours...</p>
            <p className="text-xs text-muted-foreground mt-2">Analyse du document avec B8ild AI Vision</p>
          </div>
        )}

        {/* Erreur OCR */}
        {ocrError && !isExtracting && (
          <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-destructive shrink-0 mt-0.5" />
              <div>
                <p className="font-medium text-destructive">Erreur d'extraction</p>
                <p className="text-sm text-muted-foreground mt-1">{ocrError}</p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="mt-4"
              onClick={() => document.getElementById('file-upload-content')?.click()}
            >
              Réessayer
            </Button>
          </div>
        )}

        {/* Résultat OCR */}
        {extractedData && !isExtracting && (
          <div className="space-y-4">
            {/* Badge confiance */}
            <div className="flex items-center justify-between">
              <Badge variant={confidence >= 0.8 ? 'default' : 'secondary'}>
                <Check className="h-3 w-3 mr-1" />
                Confiance: {Math.round(confidence * 100)}%
              </Badge>
              <Button variant="ghost" size="sm" onClick={resetOCR}>
                Nouveau scan
              </Button>
            </div>

            {/* Données extraites */}
            <div className="p-4 bg-muted/30 rounded-lg space-y-3">
              {extractedData.montant && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Montant</span>
                  <span className="font-semibold text-green-500">
                    {formatMontant(extractedData.montant)}
                  </span>
                </div>
              )}
              {extractedData.date_paiement && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Date</span>
                  <span>{extractedData.date_paiement}</span>
                </div>
              )}
              {extractedData.reference && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Référence</span>
                  <span>{extractedData.reference}</span>
                </div>
              )}
              {extractedData.type_paiement_detecte && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Type détecté</span>
                  <span className="capitalize">{extractedData.type_paiement_detecte}</span>
                </div>
              )}
              {extractedData.payeur_nom && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Payeur</span>
                  <span>{extractedData.payeur_nom}</span>
                </div>
              )}
              {extractedData.libelle && (
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Libellé</span>
                  <span className="text-right max-w-[200px] truncate">{extractedData.libelle}</span>
                </div>
              )}
            </div>

            {/* Bouton pour appliquer */}
            <Button onClick={applyOCRData} className="w-full">
              <Check className="h-4 w-4 mr-2" />
              Utiliser ces données
            </Button>
          </div>
        )}
      </TabsContent>
    </Tabs>
  );
}
